import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ProratedNote } from '../components/billing';
import { date, money, ownUsageOnly, usageRatio } from '../components/format';
import { Link } from 'react-router-dom';
import { Alert, Card, Spinner } from '../components/ui';
import { api } from '../services/api';
import { useSession } from '../session';
import type { OrganizationUsage } from '../types';

export function UsagePage() {
  const { t, i18n } = useTranslation();
  const { user } = useSession();
  const [usage, setUsage] = useState<OrganizationUsage | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    api.usage().then(setUsage).catch(() => setError(true));
  }, []);
  if (error) return <Alert kind="error">{t('app.error')}</Alert>;
  if (!usage || !user) return <Spinner />;
  const admin = user.organization?.role === 'owner' || user.organization?.role === 'admin';
  if (ownUsageOnly(usage, user.organization?.role)) {
    const line = usage.members.find((m) => m.user_id === user.id);
    return <OwnUsageCard usage={usage} requests={line?.request_count ?? null} locale={i18n.language} />;
  }
  return (
    <div className="flex flex-col gap-4">
      <Card title={t('usage.title')}>
        {/* Without a subscription there are no monthly credits, only what packs hold. */}
        {usage.offer ? (
          <>
            <SectionLabel>{t('usage.poolTitleOffer', { offer: usage.offer })}</SectionLabel>
            <UsageBar usage={usage} locale={i18n.language} />
          </>
        ) : (
          <p className="text-[13px] text-muted">{t('usage.noPool')}</p>
        )}
        {usage.limit_reached && (
          <div className="mt-3">
            <Alert kind="error">
              {t(`usage.reason.${usage.reason ?? 'limit_reached'}`, { cap: money(usage.user_cap ?? usage.member_cap ?? 0, usage.currency, i18n.language) })}{' '}
              {admin && (
                <Link to="/billing" className="underline">
                  {t('usage.goBilling')}
                </Link>
              )}
            </Alert>
          </div>
        )}
        <div className="mt-5 border-t border-line-row pt-4" data-testid="pack-credits">
          <SectionLabel>{t('usage.creditsTitle')}</SectionLabel>
          <span className="text-[15px] font-medium text-ink">{t('usage.creditsLeft', { credits: money(usage.credits, usage.currency, i18n.language) })}</span>
          <p className="mt-1 text-[12px] text-muted">{t('usage.creditsHint')}</p>
        </div>
        <dl className="mt-4 grid gap-x-6 gap-y-2 border-t border-line-row pt-4 text-[13px] sm:grid-cols-3">
          <div>
            <dt className="font-mono text-[10.5px] uppercase tracking-[0.06em] text-faint">{t('usage.yours')}</dt>
            <dd className="text-ink">
              {money(usage.user_spent, usage.currency, i18n.language)}
              {usage.member_cap !== null && (
                <span className="text-faint" data-testid="member-cap">
                  {' '}
                  / {money(usage.member_cap, usage.currency, i18n.language)} {usage.period === 'trial' ? t('usage.capForTrial') : t('usage.capPerMonth')}
                </span>
              )}
            </dd>
          </div>
          {usage.user_cap !== null && (
            <div>
              <dt className="font-mono text-[10.5px] uppercase tracking-[0.06em] text-faint">{t('usage.userCap')}</dt>
              <dd className="text-ink">{money(usage.user_cap, usage.currency, i18n.language)}</dd>
            </div>
          )}
        </dl>
        <p className="mt-3 text-[12px] text-faint">{t('usage.hint', { currency: usage.currency })}</p>
      </Card>
      <Card title={admin ? t('usage.perMember') : t('usage.yourLine')}>
        {usage.members.length === 0 ? (
          <p className="text-[13px] text-faint">{t('usage.noUsage')}</p>
        ) : (
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left font-mono text-[10.5px] uppercase tracking-wider text-faint">
                <th className="py-1.5 font-normal">{t('org.member')}</th>
                <th className="py-1.5 text-right font-normal">{t('usage.requests', { count: 0 }).replace(/^0 /, '')}</th>
                <th className="py-1.5 text-right font-normal">{t('invoices.amount')}</th>
                {admin && <th className="py-1.5 text-right font-normal">{t(usage.period === 'trial' ? 'org.capTrial' : 'org.cap')}</th>}
              </tr>
            </thead>
            <tbody>
              {usage.members.map((m) => (
                <tr key={m.user_id} className="border-t border-line-row">
                  <td className="py-2">
                    <div className="text-ink">{m.name}</div>
                    <div className="font-mono text-[11px] text-faint">{m.email}</div>
                  </td>
                  <td className="py-2 text-right font-mono text-[12px] text-muted">{m.request_count}</td>
                  <td className="py-2 text-right font-mono text-[12px] text-ink">{money(m.spent_usd, usage.currency, i18n.language)}</td>
                  {admin && <td className="py-2 text-right font-mono text-[12px] text-muted">{m.cap === null ? t('org.noCap') : money(m.cap, usage.currency, i18n.language)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

/** A plain member's page: their spend this period against the limit their admin set, nothing of the team's. */
function OwnUsageCard({ usage, requests, locale }: { usage: OrganizationUsage; requests: number | null; locale: string }) {
  const { t } = useTranslation();
  const c = usage.currency;
  const cap = usage.member_cap;
  const spent = usage.user_spent;
  const ratio = cap === null ? 0 : usageRatio(spent, cap, false);
  const period = usage.period === 'month' ? t('usage.periodMonth') : t('usage.periodTrial');
  // The organization's own figures stay with its admins: past its limit, a member only learns that it is spent.
  const orgSpent = usage.reason === null || usage.reason === 'limit_reached';
  return (
    <Card title={t('usage.own.title')}>
      <div className="flex flex-col gap-2" data-testid="own-usage">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className="text-[15px] font-medium text-ink">
            {cap === null ? t('usage.spentUnlimited', { spent: money(spent, c, locale) }) : t('usage.spent', { spent: money(spent, c, locale), allowance: money(cap, c, locale) })}
          </span>
          <span className="font-mono text-[11px] text-faint">
            {period}
            {usage.period_end ? ` · ${t('usage.until', { date: date(usage.period_end, locale) })}` : ''}
          </span>
        </div>
        {cap !== null && (
          <div className="h-2 w-full overflow-hidden rounded-full bg-hover" role="progressbar" aria-label={t('usage.own.bar')} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(ratio * 100)}>
            <div className={`h-full rounded-full ${ratio >= 1 ? 'bg-danger' : ratio > 0.8 ? 'bg-warn' : 'bg-accent'}`} style={{ width: `${Math.round(ratio * 100)}%` }} />
          </div>
        )}
        <div className="flex flex-wrap justify-between gap-2 text-[12px] text-muted">
          <span data-testid="own-limit">
            {cap === null
              ? t('usage.own.noLimit')
              : t(usage.period === 'trial' ? 'usage.own.limitTrial' : 'usage.own.limitMonth', { cap: money(cap, c, locale) })}
          </span>
          {requests !== null && <span>{t('usage.requests', { count: requests })}</span>}
        </div>
      </div>
      {usage.limit_reached && (
        <div className="mt-3">
          <Alert kind="error">
            {orgSpent
              ? t('usage.own.orgSpent')
              : t(`usage.reason.${usage.reason}`, { cap: money(usage.user_cap ?? usage.member_cap ?? 0, c, locale) })}
          </Alert>
        </div>
      )}
      <p className="mt-3 text-[12px] text-faint">{t('usage.own.hint', { currency: usage.currency })}</p>
    </Card>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return <h3 className="mb-2 font-mono text-[10.5px] uppercase tracking-[0.06em] text-faint">{children}</h3>;
}

export function UsageBar({ usage, locale }: { usage: OrganizationUsage; locale: string }) {
  const { t } = useTranslation();
  const ratio = usageRatio(usage.spent_usd, usage.budget_usd, usage.unlimited);
  const c = usage.currency;
  const period = usage.period === 'month' ? t('usage.periodMonth') : t('usage.periodTrial');
  return (
    <div className="flex flex-col gap-2" data-testid="usage-bar">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-[15px] font-medium text-ink">
          {usage.unlimited ? t('usage.spentUnlimited', { spent: money(usage.spent_usd, c, locale) }) : t('usage.spent', { spent: money(usage.spent_usd, c, locale), allowance: money(usage.budget_usd, c, locale) })}
        </span>
        <span className="font-mono text-[11px] text-faint">
          {period}
          {usage.period_end ? ` · ${t('usage.until', { date: date(usage.period_end, locale) })}` : ''}
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-hover" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(ratio * 100)}>
        <div className={`h-full rounded-full ${usage.limit_reached ? 'bg-danger' : ratio > 0.8 ? 'bg-warn' : 'bg-accent'}`} style={{ width: usage.unlimited ? '0%' : `${Math.round(ratio * 100)}%` }} />
      </div>
      <div className="flex flex-wrap justify-between gap-2 text-[12px] text-muted">
        <span>{usage.unlimited ? `${t('usage.allowance')}: ${t('usage.unlimited')}` : t('usage.remaining', { remaining: money(usage.remaining_usd, c, locale) })}</span>
        <span>{t('usage.requests', { count: usage.request_count })}</span>
      </div>
      {usage.prorated_since && usage.budget_full !== null && (
        <ProratedNote since={usage.prorated_since} full={usage.budget_full} periodEnd={usage.period_end} currency={c} locale={locale} />
      )}
    </div>
  );
}
